<template>
  <div class="flex flex-col items-center">
    <div class="relative inline-flex shrink-0">
      <AgentAvatar
        :variant="localVariant"
        :color="localColor"
        size="form"
        eyes-pulse
        :name="previewName || 'Agente'"
      />
      <button
        type="button"
        class="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 ring-2 ring-white/10 transition-all duration-200 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#2AD7DE]/50 focus:ring-offset-2 translate-x-0.5 translate-y-0.5"
        aria-label="Escolher avatar"
        @click="openPicker"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          stroke-width="2.25"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14" />
        </svg>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="pickerOpen"
        class="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-transparent"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-avatar-picker-title"
        @click.self="closePicker"
      >
        <div
          class="bg-[#435b7c] border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/50 w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
          @click.stop
        >
          <div class="px-4 py-3 border-b border-cyan-200/25 flex items-center justify-between gap-2 bg-[#2f4563]">
            <h4 id="agent-avatar-picker-title" class="text-base font-semibold text-slate-100">
              Escolha o Avatar
            </h4>
            <button
              type="button"
              class="p-2 rounded-lg text-slate-400 hover:bg-slate-800/70 hover:text-cyan-300 transition"
              aria-label="Fechar"
              @click="closePicker"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="overflow-y-auto px-4 py-4 space-y-5 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70">
            <div>
              <p class="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Cor</p>
              <!-- Ring usa box-shadow e não aumenta altura do layout: padding no wrapper evita cortar no overflow-y-auto -->
              <div class="-mx-1 px-1">
                <div class="flex flex-nowrap justify-between items-center gap-0 min-w-0 py-1">
                  <div
                    v-for="c in AGENT_AVATAR_COLORS"
                    :key="c.hex"
                    class="shrink-0 flex items-center justify-center px-1 py-2"
                  >
                    <button
                      type="button"
                      class="w-5 h-5 rounded-full border shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-cyan-400/60"
                      :class="
                        localColor.toLowerCase() === c.hex.toLowerCase()
                          ? 'border-slate-200 ring-2 ring-cyan-400 ring-offset-1 ring-offset-slate-900/95'
                          : 'border-slate-600 bg-slate-900/20'
                      "
                      :style="{ backgroundColor: c.hex }"
                      :title="c.label"
                      :aria-label="`Cor ${c.label}`"
                      @click="setColor(c.hex)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p class="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Estilos disponíveis</p>
              <div class="grid grid-cols-3 gap-3">
                <button
                  v-for="(vid, index) in availableVariants"
                  :key="vid"
                  type="button"
                  class="flex items-center justify-center p-1 rounded-xl border-2 transition focus:outline-none focus:ring-2 focus:ring-cyan-400/60 aspect-square min-h-0"
                  :class="
                    localVariant === vid
                      ? 'border-cyan-400/80 bg-cyan-400/10'
                      : 'border-slate-700 hover:border-slate-600 bg-slate-900/20'
                  "
                  :aria-label="`Formato ${index + 1}`"
                  @click="setVariant(vid)"
                >
                  <AgentAvatar
                    :variant="vid"
                    :color="localColor"
                    size="picker"
                    :selected="localVariant === vid"
                  />
                </button>
              </div>
            </div>
          </div>

          <div class="px-4 py-3 border-t border-slate-700/70 bg-[#394B66]">
            <button
              type="button"
              class="w-full py-2.5 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#2AD7DE]/50 transition"
              @click="closePicker"
            >
              Concluir
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import AgentAvatar from './AgentAvatar.vue'
import {
  EXECUTOR_AVATAR_VARIANTS,
  COORDINATOR_AVATAR_VARIANTS,
  AGENT_AVATAR_COLORS,
  normalizeAgentAvatarVariant,
  normalizeAgentAvatarColor,
  DEFAULT_AGENT_AVATAR_VARIANT,
  DEFAULT_AGENT_AVATAR_COLOR
} from '../constants/agentAvatarPresets.js'

export default {
  name: 'AgentAvatarPicker',
  components: { AgentAvatar },
  props: {
    variant: {
      type: String,
      default: DEFAULT_AGENT_AVATAR_VARIANT
    },
    color: {
      type: String,
      default: DEFAULT_AGENT_AVATAR_COLOR
    },
    previewName: {
      type: String,
      default: ''
    },
    agentType: {
      type: String,
      default: 'executor'
    }
  },
  emits: ['update:variant', 'update:color'],
  data() {
    return {
      EXECUTOR_AVATAR_VARIANTS,
      COORDINATOR_AVATAR_VARIANTS,
      AGENT_AVATAR_COLORS,
      localVariant: normalizeAgentAvatarVariant(this.variant),
      localColor: normalizeAgentAvatarColor(this.color),
      pickerOpen: false
    }
  },
  watch: {
    variant(v) {
      this.localVariant = normalizeAgentAvatarVariant(v)
    },
    color(c) {
      this.localColor = normalizeAgentAvatarColor(c)
    },
    agentType() {
      this.ensureVariantMatchesType()
    }
  },
  computed: {
    availableVariants() {
      return this.agentType === 'coordinator' ? this.COORDINATOR_AVATAR_VARIANTS : this.EXECUTOR_AVATAR_VARIANTS
    }
  },
  methods: {
    ensureVariantMatchesType() {
      if (this.availableVariants.includes(this.localVariant)) return
      this.localVariant = this.availableVariants[0] || DEFAULT_AGENT_AVATAR_VARIANT
      this.$emit('update:variant', this.localVariant)
    },
    openPicker() {
      this.ensureVariantMatchesType()
      this.pickerOpen = true
    },
    closePicker() {
      this.pickerOpen = false
    },
    setVariant(id) {
      this.localVariant = normalizeAgentAvatarVariant(id)
      this.$emit('update:variant', this.localVariant)
    },
    setColor(hex) {
      this.localColor = normalizeAgentAvatarColor(hex)
      this.$emit('update:color', this.localColor)
    }
  },
  mounted() {
    this.ensureVariantMatchesType()
  }
}
</script>
